import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, ShieldCheck, Upload, CheckCircle2, AlertTriangle, Plus, X, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VendorPortalPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/vendor-portal/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Portal not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const updateField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const updateContact = (idx, k, v) => {
    setData((d) => {
      const next = [...(d.contacts || [])];
      next[idx] = { ...next[idx], [k]: v };
      return { ...d, contacts: next };
    });
  };
  const addContact = () => setData((d) => ({ ...d, contacts: [...(d.contacts || []), { first_name: '', last_name: '', email: '', phone: '', notes: '' }] }));
  const removeContact = (idx) => setData((d) => ({ ...d, contacts: (d.contacts || []).filter((_, i) => i !== idx) }));

  const saveInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vendor-portal/${token}`, {
        business_name: data.business_name,
        business_address: data.business_address,
        business_phone: data.business_phone,
        business_email: data.business_email,
        contacts: data.contacts,
      });
      setSavedAt(new Date().toLocaleTimeString());
      toast.success('Vendor info saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8 MB)');
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const data_base64 = reader.result.toString().split(',').pop();
        await axios.post(`${API}/vendor-portal/${token}/insurance`, {
          filename: file.name,
          content_type: file.type,
          data_base64,
        });
        toast.success('Insurance uploaded');
        const r = await axios.get(`${API}/vendor-portal/${token}`);
        setData(r.data);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error('Upload failed');
      setUploading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>;
  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="text-xl font-bold">Portal unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 py-10 px-4" data-testid="vendor-portal-public">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <ShieldCheck className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
          <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">Vendor Portal</h1>
          <p className="text-sm text-slate-300 mt-2">Confirm your details and upload your latest certificate of insurance for <strong className="text-indigo-300">{data?.event_name || 'this event'}</strong>.</p>
        </div>

        {data?.contract_token && (
          <Card className="mb-6 border-indigo-500/40 bg-indigo-500/5">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-400" />
                <div>
                  <div className="font-semibold">Vendor Service Agreement</div>
                  <div className="text-xs text-muted-foreground">Review &amp; sign your contract</div>
                </div>
              </div>
              <Button asChild variant="outline" data-testid="open-contract-btn">
                <a href={`/contract/${data.contract_token}`}>Open contract</a>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-semibold">Business Info</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Business name</label>
                <Input value={data?.business_name || ''} onChange={(e) => updateField('business_name', e.target.value)} data-testid="biz-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Main email</label>
                <Input type="email" value={data?.business_email || ''} onChange={(e) => updateField('business_email', e.target.value)} data-testid="biz-email-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Main phone</label>
                <Input value={data?.business_phone || ''} onChange={(e) => updateField('business_phone', e.target.value)} data-testid="biz-phone-input" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Address</label>
                <Input value={data?.business_address || ''} onChange={(e) => updateField('business_address', e.target.value)} data-testid="biz-address-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Contacts</h2>
              <Button size="sm" variant="outline" onClick={addContact} data-testid="add-contact-btn"><Plus className="w-4 h-4 mr-1" /> Add contact</Button>
            </div>
            {(data?.contacts || []).length === 0 && <p className="text-xs text-muted-foreground">No contacts yet. Add at least one primary point of contact.</p>}
            {(data?.contacts || []).map((c, i) => (
              <div key={i} className="border border-border rounded-lg p-3 space-y-2" data-testid={`contact-row-${i}`}>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input placeholder="First name" value={c.first_name || ''} onChange={(e) => updateContact(i, 'first_name', e.target.value)} />
                  <Input placeholder="Last name" value={c.last_name || ''} onChange={(e) => updateContact(i, 'last_name', e.target.value)} />
                  <Input type="email" placeholder="Email" value={c.email || ''} onChange={(e) => updateContact(i, 'email', e.target.value)} />
                  <Input placeholder="Phone" value={c.phone || ''} onChange={(e) => updateContact(i, 'phone', e.target.value)} />
                </div>
                <Textarea placeholder="Notes" rows={2} value={c.notes || ''} onChange={(e) => updateContact(i, 'notes', e.target.value)} />
                <div className="flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => removeContact(i)}><X className="w-4 h-4 mr-1" /> Remove</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-6 space-y-3">
            <h2 className="font-semibold">Certificate of Insurance</h2>
            {data?.has_insurance ? (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>{data.insurance_filename}</span>
                <span className="text-xs text-muted-foreground ml-auto">Uploaded {data.insurance_uploaded_at ? new Date(data.insurance_uploaded_at).toLocaleString() : ''}</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No COI on file. Upload your latest certificate of insurance (PDF, JPG, PNG — max 8 MB).</p>
            )}
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} className="hidden" data-testid="insurance-file-input" disabled={uploading} />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {data?.has_insurance ? 'Replace insurance' : 'Upload insurance'}
              </span>
            </label>
          </CardContent>
        </Card>

        <Button
          size="lg"
          className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 h-12"
          disabled={saving}
          onClick={saveInfo}
          data-testid="save-portal-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          Save vendor info
        </Button>
        {savedAt && <p className="text-center text-xs text-emerald-400 mt-2">Saved at {savedAt}</p>}
      </div>
    </div>
  );
}
